import './ConfigurableLeaderboard.css';
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
    fetchUnifiedLeaderboardData,
    normalizeSessionId,
    parseExcludedSessionIds,
} from '../shared/leaderboardShared';
import { decodeDraftConfig, DRAFT_PARAM, DRAFT_SLUG } from '../shared/draftConfig';
import { loadLeaderboardConfig, normalizeLeaderboardConfig } from './leaderboardConfig';

function formatPoints(value, decimals) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '0';
    return decimals > 0 ? parsed.toFixed(decimals) : String(Math.round(parsed));
}

function formatRank(place, rankStyle) {
    if (rankStyle === 'hash') return `#${place}`;
    return String(place);
}

function PlayerName({ alive, flagSize: forcedFlagSize, memberData, rowFontSize, showAliveIndicator, showFlags, teamname }) {
    const displayLabel = showFlags && memberData?.length
        ? memberData.map((member) => member.name).join(' - ')
        : teamname;
    const playerCount = showFlags && memberData?.length
        ? memberData.length
        : displayLabel.split(' - ').length;
    const baseSize = playerCount >= 3 ? Math.min(rowFontSize, 18) : rowFontSize;
    const pivot = playerCount >= 3 ? 14 : 18;
    const shrinkPerChar = playerCount >= 3 ? 0.62 : 0.48;
    const fontSize = Math.max(8, Math.min(rowFontSize, Math.round(
        baseSize - Math.max(0, displayLabel.length - pivot) * shrinkPerChar
    )));
    const flagSize = forcedFlagSize > 0
        ? forcedFlagSize
        : Math.max(12, Math.min(28, fontSize + 2));
    const nameRef = useRef(null);
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        const element = nameRef.current;
        if (!element) return;

        const availableWidth = element.clientWidth;
        const previousWidth = element.style.width;
        const previousTransform = element.style.transform;
        element.style.transform = 'none';
        element.style.width = 'max-content';
        const neededWidth = element.scrollWidth;
        element.style.width = previousWidth;
        element.style.transform = previousTransform;

        setScale(availableWidth > 0 && neededWidth > availableWidth
            ? Math.max(0.45, availableWidth / neededWidth)
            : 1);
    }, [displayLabel, flagSize, fontSize, showFlags]);

    return (
        <span
            ref={nameRef}
            className="configurable-team-name"
            style={{
                fontSize: `${fontSize}px`,
                letterSpacing: fontSize >= 20 ? '0.6px' : '0.3px',
                transform: `scale(${scale})`,
                transformOrigin: 'center',
            }}
        >
            {showAliveIndicator && alive && (
                <span className="configurable-alive-dot" aria-label="Encore en vie" />
            )}
            {showFlags && memberData?.length ? memberData.map((member, index) => (
                <span key={`${member.epicId || member.name}-${index}`} className="configurable-member">
                    <img
                        src={member.flagUrl}
                        alt={member.countryName ? `Drapeau ${member.countryName}` : 'Drapeau'}
                        className="configurable-flag"
                        style={{ width: `${flagSize}px`, height: `${flagSize}px` }}
                        onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = member.fallbackFlagUrl;
                        }}
                    />
                    <span>{member.name}</span>
                    {index < memberData.length - 1 && <span className="configurable-separator"> - </span>}
                </span>
            )) : teamname}
        </span>
    );
}

function PositionIndicator({ alive, games, positionChange, visible }) {
    if (!visible || alive || games < 2 || positionChange === null) return null;
    const className = positionChange === 0
        ? 'neutral'
        : (positionChange > 0 ? 'positive' : 'negative');
    const label = positionChange > 0 ? `+${positionChange}` : String(positionChange || '=');
    return <span className={`configurable-position-indicator ${className}`}>{label}</span>;
}

function LeaderboardRow({ animation, config, data, index, onClick, showPositionIndicators }) {
    if (data.empty) {
        const { display: emptyDisplay } = config;
        const emptyStats = [
            emptyDisplay.showAvgPlace,
            emptyDisplay.showElims,
            emptyDisplay.showWins && emptyDisplay.winsAsColumn,
            emptyDisplay.showPoints,
            emptyDisplay.showGames,
        ].filter(Boolean);

        return (
            <div className="configurable-row configurable-row-empty">
                <div className="configurable-row-accent" />
                {emptyDisplay.showRank && <div className="configurable-rank" />}
                <div className="configurable-name" />
                {emptyStats.map((value, statIndex) => (
                    <div key={`stat-${statIndex}`} className="configurable-stat" />
                ))}
            </div>
        );
    }

    const { display, theme } = config;
    const rankClass = display.highlightTop3 && data.place <= 3 ? ` top-${data.place}` : '';
    const deadClass = display.dimDeadRows && !data.alive ? ' is-dead' : '';
    const delay = animation.style === 'none'
        ? '0s'
        : `${Math.min(index * (animation.staggerMs / 1000), 1.2)}s`;

    return (
        <div
            className={`configurable-row${rankClass}${deadClass}`}
            style={{ animationDelay: delay }}
        >
            <div className="configurable-row-accent" />
            {display.showRank && (
                <div className={`configurable-rank${rankClass}`}>
                    {formatRank(data.place, display.rankStyle)}
                    <PositionIndicator
                        alive={data.alive}
                        games={data.games}
                        positionChange={data.positionChange}
                        visible={showPositionIndicators}
                    />
                </div>
            )}
            <button type="button" className="configurable-name" onClick={onClick}>
                <PlayerName
                    alive={data.alive}
                    flagSize={display.flagSize}
                    memberData={data.memberData}
                    rowFontSize={theme.rowFontSize}
                    showAliveIndicator={config.showAliveIndicator}
                    showFlags={config.showFlags}
                    teamname={data.teamname}
                />
                {display.showWins && !display.winsAsColumn && (
                    <span className="configurable-wins">{data.wins || '-'}</span>
                )}
            </button>
            {display.showAvgPlace && (
                <div className="configurable-stat">
                    {Number(data.avg_place || 0).toFixed(display.avgPlaceDecimals)}
                </div>
            )}
            {display.showElims && <div className="configurable-stat">{data.elims}</div>}
            {display.showWins && display.winsAsColumn && (
                <div className="configurable-stat">{data.wins}</div>
            )}
            {display.showPoints && (
                <div className="configurable-stat configurable-points">
                    {formatPoints(data.points, display.pointsDecimals)}
                </div>
            )}
            {display.showGames && <div className="configurable-stat">{data.games}</div>}
        </div>
    );
}

function LeaderboardHeader({ config, onNext, onPrevious }) {
    const { display, labels } = config;
    return (
        <div className="configurable-header">
            {display.showRank && (
                <button type="button" className="configurable-rank-header" onClick={onPrevious}>
                    {labels.rank}
                </button>
            )}
            <div className="configurable-name-header">
                <span>{labels.players}</span>
                {display.showWins && !display.winsAsColumn && (
                    <span className="configurable-wins-label">{labels.wins}</span>
                )}
            </div>
            {display.showAvgPlace && <div className="configurable-stat-header">{labels.avgPlace}</div>}
            {display.showElims && <div className="configurable-stat-header">{labels.eliminations}</div>}
            {display.showWins && display.winsAsColumn && (
                <div className="configurable-stat-header">{labels.wins}</div>
            )}
            {display.showPoints && (
                <button type="button" className="configurable-stat-header" onClick={onNext}>
                    {labels.points}
                </button>
            )}
            {display.showGames && <div className="configurable-stat-header">{labels.games}</div>}
        </div>
    );
}

function LeaderboardColumn({ animation, config, onNext, onPrevious, onSelect, rows, showPositionIndicators, variant }) {
    return (
        <section className={`configurable-column${variant === 'second' ? ' configurable-second-column' : ''}`}>
            {config.display.showHeader && (
                <LeaderboardHeader config={config} onNext={onNext} onPrevious={onPrevious} />
            )}
            {rows.map((data, index) => (
                <LeaderboardRow
                    key={data.empty ? `empty-${variant}-${index}` : `${data.teamname}-${data.place}-${variant}`}
                    animation={animation}
                    config={config}
                    data={data}
                    index={index}
                    showPositionIndicators={showPositionIndicators}
                    onClick={() => onSelect(data.teamname)}
                />
            ))}
        </section>
    );
}

function TeamDetailsModal({ details, teamName, onClose }) {
    if (!details || !teamName) return null;
    return (
        <div className="configurable-modal-backdrop" onClick={onClose} role="presentation">
            <div
                className="configurable-modal"
                role="dialog"
                aria-modal="true"
                aria-label={`Détails de ${teamName}`}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="configurable-modal-header">
                    <h2>{teamName}</h2>
                    <button type="button" onClick={onClose} aria-label="Fermer">×</button>
                </div>
                <strong>Membres</strong>
                <ul>
                    {details.members.map((member, index) => (
                        <li key={`${member.id || member.name}-${index}`}>{member.name}</li>
                    ))}
                </ul>
                <strong>Parties</strong>
                <div className="configurable-games">
                    {details.sessions.map((session, index) => (
                        <div key={session.id || `session-${index}`}>
                            <span>Partie {index + 1}</span>
                            <span>Place : {session.place}</span>
                            <span>Éliminations : {session.kills}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StatusScreen({ message }) {
    return (
        <div className="configurable-status" role="status">
            <div>{message}</div>
        </div>
    );
}

function useCustomFont(fontFamily, fontFile) {
    useEffect(() => {
        if (!fontFile || !fontFamily || typeof FontFace === 'undefined') return undefined;
        const customFont = new FontFace(fontFamily, `url("${fontFile}")`);
        let registered = false;

        customFont.load()
            .then((loadedFont) => {
                document.fonts.add(loadedFont);
                registered = true;
            })
            .catch((error) => console.error('Error loading custom font:', error));

        return () => {
            if (registered) document.fonts.delete(customFont);
        };
    }, [fontFamily, fontFile]);
}

export function buildLeaderboardStyle(config) {
    const { animation, display, layout, theme } = config;
    const autoWidths = layout.columns === 2
        ? { rank: 120, name: 520, stat: 85 }
        : { rank: 132, name: 598, stat: 98 };
    const rankWidth = layout.rankWidth || autoWidths.rank;
    const nameWidth = layout.nameWidth || autoWidths.name;
    const statWidth = layout.statWidth || autoWidths.stat;

    const rowsInTallestColumn = Math.ceil(layout.teamsPerPage / layout.columns);
    const headerBlock = display.showHeader ? layout.headerHeight + 6 : 0;
    const panelBlock = display.showPanel ? layout.panelPadding * 2 : 0;
    const columnContentHeight = panelBlock + headerBlock
        + rowsInTallestColumn * (layout.rowHeight + layout.rowGap);
    const tableSafeArea = Math.min(
        Math.round(layout.canvasHeight * 0.36),
        Math.max(
            Math.round(layout.canvasHeight * 0.06),
            layout.titleTop + theme.titleFontSize * 1.2
                + (config.subtitle ? theme.subtitleFontSize * 1.3 : 0)
                + layout.tableTop
        )
    );
    const tableBottomArea = layout.verticalAlign === 'center' ? tableSafeArea : 0;
    const availableTableHeight = Math.max(
        240,
        layout.canvasHeight - tableSafeArea - tableBottomArea
    );
    const preferredColumnScale = layout.columns === 1 ? 1.15 : 1;
    const autoScale = layout.autoScale
        ? Math.max(0.45, Math.min(preferredColumnScale, availableTableHeight / columnContentHeight))
        : 1;
    const columnScale = Math.max(0.2, autoScale * layout.scale);

    const flagRadius = display.flagShape === 'circle'
        ? '50%'
        : (display.flagShape === 'rounded' ? '4px' : '0');

    return {
        '--lb-accent': theme.accentColor,
        '--lb-alive': theme.aliveColor,
        '--lb-alternate-row': theme.alternateRowColor,
        '--lb-anim-duration': `${animation.durationMs}ms`,
        '--lb-background': theme.backgroundColor,
        '--lb-cell-gap': `${layout.cellGap}px`,
        '--lb-column-gap': `${layout.columnGap}px`,
        '--lb-column-scale': columnScale,
        '--lb-row-border-width': `${layout.rowBorderWidth}px`,
        '--lb-dead-opacity': display.deadRowOpacity,
        '--lb-flag-radius': flagRadius,
        '--lb-font': `'${theme.fontFamily}', sans-serif`,
        '--lb-header-end': theme.headerEndColor,
        '--lb-header-font-size': `${theme.headerFontSize}px`,
        '--lb-header-letter-spacing': `${theme.headerLetterSpacing}px`,
        '--lb-header-height': `${layout.headerHeight}px`,
        '--lb-header-start': theme.headerStartColor,
        '--lb-header-text': theme.headerTextColor,
        '--lb-muted-text': theme.mutedTextColor,
        '--lb-name-width': `${nameWidth}px`,
        '--lb-negative': theme.negativeColor,
        '--lb-neutral': theme.neutralColor,
        '--lb-panel': display.showPanel ? theme.panelColor : 'transparent',
        '--lb-panel-border': theme.panelBorderColor
            || `color-mix(in srgb, ${theme.accentColor} 28%, transparent)`,
        '--lb-panel-padding': `${layout.panelPadding}px`,
        '--lb-panel-radius': `${layout.panelRadius}px`,
        '--lb-panel-shadow': display.showPanel ? theme.panelShadow : 'none',
        '--lb-positive': theme.positiveColor,
        '--lb-rank-font': `'${theme.rankFontFamily}', '${theme.fontFamily}', sans-serif`,
        '--lb-rank-width': `${rankWidth}px`,
        '--lb-row': theme.rowColor,
        '--lb-row-alternate': display.stripedRows ? theme.alternateRowColor : theme.rowColor,
        '--lb-row-border': theme.rowBorderColor
            || `color-mix(in srgb, ${theme.accentColor} 18%, transparent)`,
        '--lb-row-font-size': `${theme.rowFontSize}px`,
        '--lb-row-gap': `${layout.rowGap}px`,
        '--lb-row-height': `${layout.rowHeight}px`,
        '--lb-row-letter-spacing': `${theme.rowLetterSpacing}px`,
        '--lb-row-radius': `${layout.rowRadius}px`,
        '--lb-stat-width': `${statWidth}px`,
        '--lb-subtitle-color': theme.subtitleColor,
        '--lb-subtitle-font-size': `${theme.subtitleFontSize}px`,
        '--lb-table-bottom': `${tableBottomArea}px`,
        '--lb-table-safe-area': `${tableSafeArea}px`,
        '--lb-text': theme.textColor,
        '--lb-text-shadow': theme.textShadow,
        '--lb-title-color': theme.titleColor,
        '--lb-title-font': theme.titleFontFamily
            ? `'${theme.titleFontFamily}', '${theme.fontFamily}', sans-serif`
            : `'${theme.fontFamily}', sans-serif`,
        '--lb-title-font-size': `${theme.titleFontSize}px`,
        '--lb-title-letter-spacing': `${theme.titleLetterSpacing}px`,
        '--lb-title-shadow': theme.titleTextShadow,
        '--lb-title-top': `${layout.titleTop}px`,
        '--lb-top1': theme.top1Color,
        '--lb-top2': theme.top2Color,
        '--lb-top3': theme.top3Color,
        width: `${layout.canvasWidth}px`,
        minHeight: `${layout.canvasHeight}px`,
        backgroundColor: display.transparentBackground ? 'transparent' : theme.backgroundColor,
        backgroundImage: display.transparentBackground || !theme.backgroundImage
            ? 'none'
            : `url("${theme.backgroundImage}")`,
        backgroundPosition: theme.backgroundPosition,
        backgroundSize: theme.backgroundSize,
    };
}

function ConfigurableLeaderboard({
    configOverride = null,
    disableFetch = false,
    previewDetails = null,
    previewRows = null,
    previewMode = false,
}) {
    const { slug } = useParams();
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const [loadedConfig, setLoadedConfig] = useState(null);
    const [configError, setConfigError] = useState('');
    const [dataError, setDataError] = useState('');
    const [leaderboard, setLeaderboard] = useState([]);
    const [teamDetails, setTeamDetails] = useState({});
    const [allDeadIndicators, setAllDeadIndicators] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [page, setPage] = useState(0);

    useEffect(() => {
        if (configOverride) return undefined;

        const controller = new AbortController();
        setLoadedConfig(null);
        setConfigError('');
        setDataError('');
        setPage(0);

        if (slug === DRAFT_SLUG) {
            const draft = decodeDraftConfig(searchParams.get(DRAFT_PARAM));
            try {
                if (!draft) throw new Error('Le brouillon de configuration est illisible.');
                setLoadedConfig(normalizeLeaderboardConfig(draft, {
                    slug: 'draft',
                    configUrl: window.location.href,
                }));
            } catch (error) {
                setConfigError(error.message);
            }
            return () => controller.abort();
        }

        loadLeaderboardConfig(slug, { signal: controller.signal })
            .then(setLoadedConfig)
            .catch((error) => {
                if (error.name !== 'AbortError') setConfigError(error.message);
            });

        return () => controller.abort();
    }, [configOverride, searchParams, slug]);

    const config = configOverride || loadedConfig;

    useCustomFont(config?.theme.fontFamily, config?.theme.fontFile);
    useCustomFont(config?.theme.titleFontFamily, config?.theme.titleFontFile);

    useEffect(() => {
        if (previewMode || !config?.display.transparentBackground) return undefined;
        const previous = document.body.style.background;
        document.body.style.background = 'transparent';
        return () => {
            document.body.style.background = previous;
        };
    }, [config?.display.transparentBackground, previewMode]);

    const leaderboardId = searchParams.get('id') || config?.leaderboardId || '';
    const flagsOverride = searchParams.get('flags');
    const showFlags = flagsOverride === null
        ? Boolean(config?.showFlags)
        : flagsOverride === 'true';

    const excludedSessionIds = useMemo(() => {
        const ids = parseExcludedSessionIds(searchParams);
        (config?.excludedSessionIds || []).forEach((value) => {
            const normalized = normalizeSessionId(value);
            if (normalized) ids.add(normalized);
        });
        return ids;
    }, [config?.excludedSessionIds, searchParams]);

    const configRef = useRef(config);
    configRef.current = config;

    const excludedKey = useMemo(
        () => [...excludedSessionIds].sort().join(','),
        [excludedSessionIds]
    );
    const includeV7 = Boolean(config?.includeV7);
    const sortMode = config?.sortMode || 'wls';
    const refreshIntervalMs = config?.refreshIntervalMs || 5000;

    const fetchData = useCallback(async () => {
        const current = configRef.current;
        if (!current || !leaderboardId || disableFetch) return;
        try {
            const data = await fetchUnifiedLeaderboardData({
                leaderboardId,
                excludedSessionIds: new Set(excludedKey ? excludedKey.split(',') : []),
                showFlags,
                forceRankByPoints: sortMode === 'points',
                sortMode,
                includeV7,
                indicatorsOnlyWhenAllDead: true,
            });
            setLeaderboard(data.leaderboard);
            setTeamDetails(data.teamDetails);
            setAllDeadIndicators(data.showPositionIndicators);
            setDataError('');
        } catch (error) {
            console.error('Error loading configurable leaderboard:', error);
            setDataError('Impossible de charger les données du leaderboard.');
        }
    }, [disableFetch, excludedKey, includeV7, leaderboardId, showFlags, sortMode]);

    useEffect(() => {
        if (disableFetch || !leaderboardId) return undefined;
        let cancelled = false;
        let timeoutId;

        const refresh = async () => {
            await fetchData();
            if (!cancelled) timeoutId = window.setTimeout(refresh, refreshIntervalMs);
        };
        refresh();

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [disableFetch, fetchData, leaderboardId, refreshIntervalMs]);

    const visibleRows = useMemo(() => {
        const source = leaderboard.length ? leaderboard : (previewRows || []);
        if (!config) return source;
        const start = Math.max(0, config.pagination.startRank - 1);
        const sliced = source.slice(start);
        return config.pagination.maxTeams > 0
            ? sliced.slice(0, config.pagination.maxTeams)
            : sliced;
    }, [config, leaderboard, previewRows]);

    const teamsPerPage = config?.layout.teamsPerPage || 10;
    const maxPage = useMemo(() => {
        if (visibleRows.length <= teamsPerPage) return 0;
        return Math.ceil(visibleRows.length / teamsPerPage) - 1;
    }, [teamsPerPage, visibleRows.length]);

    useEffect(() => {
        setPage((current) => Math.min(current, maxPage));
    }, [maxPage]);

    useEffect(() => {
        if (!config?.pagination.auto || maxPage < 1) return undefined;
        const interval = window.setInterval(() => {
            setPage((current) => (current + 1) % (maxPage + 1));
        }, config.pagination.intervalSeconds * 1000);
        return () => window.clearInterval(interval);
    }, [config?.pagination.auto, config?.pagination.intervalSeconds, maxPage]);

    if (configError) return <StatusScreen message={configError} />;
    if (!config) return <StatusScreen message="Chargement du leaderboard…" />;

    const { animation, display, layout } = config;
    const showPositionIndicators = display.positionIndicators === 'always'
        || (display.positionIndicators === 'auto' && allDeadIndicators);

    const pageRows = visibleRows.slice(page * teamsPerPage, (page + 1) * teamsPerPage);
    const paddedRows = display.fillEmptyRows && pageRows.length < teamsPerPage
        ? pageRows.concat(Array.from(
            { length: teamsPerPage - pageRows.length },
            () => ({ empty: true })
        ))
        : pageRows;
    const firstColumnSize = layout.columns === 2
        ? Math.ceil(teamsPerPage / 2)
        : teamsPerPage;
    const firstRows = paddedRows.slice(0, firstColumnSize);
    const secondRows = layout.columns === 2 ? paddedRows.slice(firstColumnSize) : [];

    const rootClassNames = [
        'configurable-leaderboard',
        `anim-${animation.style}`,
        display.showPanel ? '' : 'no-panel',
        display.showRowAccent ? '' : 'no-accent',
        display.stripedRows ? '' : 'no-stripes',
        display.rowGradient ? '' : 'no-row-gradient',
        `name-case-${display.nameCase}`,
        display.dimDeadRows ? 'dim-dead' : '',
        previewMode ? 'is-preview' : '',
    ].filter(Boolean).join(' ');

    const onPrevious = () => setPage((current) => Math.max(0, current - 1));
    const onNext = () => setPage((current) => Math.min(maxPage, current + 1));
    const onSelect = (teamname) => {
        if (display.teamDetails && teamname) setSelectedTeam(teamname);
    };

    return (
        <main className={rootClassNames} style={buildLeaderboardStyle(config)}>
            {config.customCss && <style>{config.customCss}</style>}
            {config.theme.logoImage && (
                <img
                    src={config.theme.logoImage}
                    alt=""
                    className={`configurable-logo align-${config.theme.logoAlign}`}
                    style={{ width: `${config.theme.logoWidth}px`, top: `${config.theme.logoTop}px` }}
                />
            )}
            {config.title && <header className="configurable-title">{config.title}</header>}
            {config.subtitle && <div className="configurable-subtitle">{config.subtitle}</div>}
            {dataError && !previewMode && <div className="configurable-data-error">{dataError}</div>}
            <div className={`configurable-columns ${layout.columns === 1 ? 'single-column' : 'two-columns'} align-${layout.verticalAlign}`}>
                <LeaderboardColumn
                    animation={animation}
                    config={config}
                    rows={firstRows}
                    showPositionIndicators={showPositionIndicators}
                    variant="first"
                    onNext={onNext}
                    onPrevious={onPrevious}
                    onSelect={onSelect}
                />
                {layout.columns === 2 && (
                    <LeaderboardColumn
                        animation={animation}
                        config={config}
                        rows={secondRows}
                        showPositionIndicators={showPositionIndicators}
                        variant="second"
                        onNext={onNext}
                        onPrevious={onPrevious}
                        onSelect={onSelect}
                    />
                )}
            </div>
            <TeamDetailsModal
                details={selectedTeam ? (teamDetails[selectedTeam] || previewDetails?.[selectedTeam]) : null}
                teamName={selectedTeam}
                onClose={() => setSelectedTeam(null)}
            />
        </main>
    );
}

export default ConfigurableLeaderboard;
